import {
  ColumnSelect,
  enumKeyToSelectInput
} from 'lib/components/ColumnSelect';
import { TextField } from 'lib/components/TextField';
import { OrganizationStatus } from 'lib/enums';
import { EOrganization } from 'lib/types';
import React, { useState } from 'react';
import {
  CardGridLayout,
  GridInput,
  useEditableCard
} from 'lib/components/Card/Grid';
import { SwitchControlledCard } from 'lib/components/Card/SwitchControlledCard';
import LabeledSwitch from 'lib/components/LabeledSwitch';
import { Form } from 'lib/components/Form';
import { logInfo, logWarn } from 'utils/logger';
import { removeUndefinedFields } from 'lib/helpers';
import { useActiveOrganizationListener } from 'hooks/useActiveOrganizationListener';
import { useAppSelector } from 'redux/hooks';
import { selectUser } from 'redux/auth';
import { Alert } from 'lib/components/Alert';
import OrderNumberSettings from './OrderNumberSettings';
import { ENOTICE_ACCOUNT_ID } from '../../../../../constants';

export default function ImplementationInfo() {
  const activeOrganization = useActiveOrganizationListener();
  const user = useAppSelector(selectUser);

  const {
    organizationStatus,
    disabled,
    alwaysShowInPlacementFlow,
    shouldAutoGenOrderNums,
    orderNumberGeneration,
    numberOfOrders,
    accountId
  } = activeOrganization?.data() ?? {};

  const {
    formSettings,
    onChange,
    disabled: formDisabled,
    editable,
    onSubmitWrapper
  } = useEditableCard<Partial<EOrganization>>({
    organizationStatus,
    disabled,
    alwaysShowInPlacementFlow,
    shouldAutoGenOrderNums,
    orderNumberGeneration,
    /**
     * Leave this undefined so that it doesn't get overwritten by other changes,
     * and only gets put into form settings if a user actively changes it.
     * (See ONCALL-5683)
     */
    numberOfOrders: undefined
  });

  const [userError, setUserError] = useState('');
  const isDefaultAccount = accountId === ENOTICE_ACCOUNT_ID;

  const validateChange = (nextStatus: number) => {
    const isLiveAttempt = nextStatus === OrganizationStatus.live.value;
    if (isLiveAttempt && isDefaultAccount) {
      setUserError(
        'Cannot set publisher status to Live until Stripe account ID is changed from default.'
      );
      logWarn(
        'Blocked attempt to set organizationStatus Live while on default account',
        {
          orgId: activeOrganization?.id,
          userId: user?.id,
          userEmail: user?.data().email
        }
      );
      return false;
    }
    setUserError('');
    return true;
  };

  return (
    <Form
      id="publisher-settings-implementation"
      onSubmit={() =>
        onSubmitWrapper(async () => {
          if (
            formSettings.organizationStatus !==
            activeOrganization?.data().organizationStatus
          ) {
            logInfo('organizationStatus changed by user', {
              orgId: activeOrganization?.id,
              userId: user?.id,
              userEmail: user?.data().email,
              priorOrgStatus: activeOrganization?.data().organizationStatus,
              newOrgStatus: formSettings.organizationStatus
            });
          }
          if (formSettings.numberOfOrders !== numberOfOrders) {
            logInfo('numberOfOrders changed by user', {
              orgId: activeOrganization?.id,
              userId: user?.id,
              userEmail: user?.data().email,
              priorNumberOfOrders: numberOfOrders,
              newNumberOfOrders: formSettings.numberOfOrders
            });
          }
          await activeOrganization?.ref.update(
            removeUndefinedFields(formSettings)
          );
          return { success: true };
        })
      }
    >
      <CardGridLayout
        header={{ title: 'Implementation-Only Settings' }}
        editable={editable}
      >
        {userError && (
          <GridInput fullWidth>
            <Alert
              id="implementation-info-live-blocked"
              status="warning"
              description={userError}
            />
          </GridInput>
        )}
        <GridInput fullWidth>
          <div className="space-y-2">
            <ColumnSelect
              id="publisher-status"
              labelText="Publisher status"
              value={formSettings.organizationStatus?.toString() ?? ''}
              onChange={orgStatusStr => {
                const next = Number(orgStatusStr);
                if (!validateChange(next)) {
                  return; // blocked
                }
                onChange({ organizationStatus: next });
              }}
              options={OrganizationStatus.items().map(enumKeyToSelectInput)}
              disabled={formDisabled}
            />
          </div>
        </GridInput>
        <GridInput fullWidth>
          <LabeledSwitch
            label="Disable publication?"
            description="If disabled, the newspaper will not appear in the placement flow for customers."
            value={!!formSettings.disabled}
            onChange={disabled => onChange({ disabled })}
            disabled={formDisabled}
          />
        </GridInput>
        <GridInput fullWidth>
          <LabeledSwitch
            label="Always show in placement flow?"
            description="If enabled, this publication will appear in the placement flow even if missing affidavit or template settings."
            value={!!formSettings.alwaysShowInPlacementFlow}
            onChange={alwaysShowInPlacementFlow =>
              onChange({
                alwaysShowInPlacementFlow
              })
            }
            disabled={formDisabled}
          />
        </GridInput>
        <SwitchControlledCard
          labelProps={{
            label: 'Auto-generate order numbers?',
            description:
              'If enabled, Column will automatically apply order numbers to notices in this publication.',
            value: !!formSettings.shouldAutoGenOrderNums,
            onChange: shouldAutoGenOrderNums =>
              onChange({
                shouldAutoGenOrderNums
              })
          }}
          header="order number settings"
          disabled={formDisabled}
        >
          <OrderNumberSettings
            value={formSettings.orderNumberGeneration}
            onChange={orderNumberGeneration =>
              onChange({ orderNumberGeneration })
            }
            disabled={formDisabled}
          />
          <GridInput>
            <TextField
              id="order-number-next-order"
              labelText="Next order number"
              type="number"
              min={1}
              value={
                /**
                 * Add 1 so the UI shows the next order number that will be
                 * generated based on current number of orders.
                 */
                (
                  (formSettings.numberOfOrders ?? numberOfOrders ?? 1) + 1
                ).toString()
              }
              onChange={numberOfOrdersStr =>
                onChange({
                  /**
                   * Subtract 1 from the input so the number of orders is set
                   * up so the next order number is the user's input.
                   */
                  numberOfOrders: Number(numberOfOrdersStr) - 1
                })
              }
              disabled={formDisabled}
            />
          </GridInput>
        </SwitchControlledCard>
      </CardGridLayout>
    </Form>
  );
}
